import { sessionsService } from "../services/sessionsService";

/**
 * Audio Chunk Uploader
 * Collects audio during recording and uploads as single chunk at the end
 */
export class AudioChunkUploader {
  private sessionId: number;
  private mediaRecorder: MediaRecorder | null = null;
  private mediaStream: MediaStream | null = null;
  private audioChunks: Blob[] = [];
  private isPaused = false;
  private blockId = "block-001"; // Single block ID since we upload once

  constructor(sessionId: number) {
    this.sessionId = sessionId;
  }

  /**
   * Start collecting audio chunks
   */
  async start(): Promise<void> {
    try {
      // Get audio stream
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });

      // Create MediaRecorder to collect chunks
      this.mediaRecorder = new MediaRecorder(this.mediaStream, {
        mimeType: "audio/webm;codecs=opus",
      });

      console.log(
        `[AudioChunkUploader] Starting audio collection for session ${this.sessionId}`
      );

      // Collect chunks in memory
      this.mediaRecorder.addEventListener(
        "dataavailable",
        (event: BlobEvent) => {
          if (event.data.size > 0) {
            this.audioChunks.push(event.data);
            console.log(
              `[AudioChunkUploader] Collected chunk (${event.data.size} bytes), total chunks: ${this.audioChunks.length}`
            );
          }
        }
      );

      // Start recording with small timeslice to collect data periodically
      // This ensures we don't lose data if recording stops unexpectedly
      this.mediaRecorder.start(1000); // Collect every 1 second
      console.log("[AudioChunkUploader] Started collecting audio chunks");
    } catch (error) {
      console.error("[AudioChunkUploader] Failed to start:", error);
      throw error;
    }
  }

  /**
   * Pause audio collection
   */
  pause(): void {
    if (this.mediaRecorder && this.mediaRecorder.state === "recording") {
      this.mediaRecorder.pause();
      this.isPaused = true;
      console.log("[AudioChunkUploader] Paused audio collection");
    }
  }

  /**
   * Resume audio collection
   */
  resume(): void {
    if (this.mediaRecorder && this.mediaRecorder.state === "paused") {
      this.mediaRecorder.resume();
      this.isPaused = false;
      console.log("[AudioChunkUploader] Resumed audio collection");
    }
  }

  /**
   * Stop collecting and upload all audio as single chunk
   */
  async stop(): Promise<void> {
    console.log(
      `[AudioChunkUploader] Stopping audio collection for session ${this.sessionId}`
    );

    // Stop MediaRecorder and wait for final chunks
    if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
      // If paused, resume first to ensure all data is captured correctly
      if (this.mediaRecorder.state === "paused") {
        console.log(
          "[AudioChunkUploader] Resuming from paused state before stopping"
        );
        this.mediaRecorder.resume();
      }

      // Wait for final dataavailable event
      await new Promise<void>((resolve) => {
        if (this.mediaRecorder) {
          this.mediaRecorder.addEventListener("stop", () => {
            console.log("[AudioChunkUploader] MediaRecorder stopped");
            resolve();
          });
          this.mediaRecorder.stop();
        } else {
          resolve();
        }
      });
    }

    // Stop all tracks in the media stream
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }

    // Upload all collected chunks as single blob
    if (this.audioChunks.length > 0) {
      const totalBlob = new Blob(this.audioChunks, { type: "audio/webm" });
      console.log(
        `[AudioChunkUploader] Uploading complete recording (${totalBlob.size} bytes) as ${this.blockId}`
      );

      try {
        await sessionsService.uploadAudioChunk(
          this.sessionId,
          totalBlob,
          this.blockId
        );
        console.log(
          `[AudioChunkUploader] ✓ Audio uploaded successfully as ${this.blockId}`
        );
      } catch (error) {
        console.error("[AudioChunkUploader] ✗ Failed to upload audio:", error);
        throw error;
      }
    } else {
      console.warn("[AudioChunkUploader] No audio chunks to upload");
    }

    // Clean up
    this.mediaRecorder = null;
    console.log(
      `[AudioChunkUploader] Total audio size: ${this.audioChunks.reduce(
        (sum, chunk) => sum + chunk.size,
        0
      )} bytes`
    );
  }

  /**
   * Get the single block ID
   */
  getBlockIds(): string[] {
    return [this.blockId];
  }

  /**
   * Get collected audio size
   */
  getCollectedSize(): number {
    return this.audioChunks.reduce((sum, chunk) => sum + chunk.size, 0);
  }

  /**
   * Check if paused
   */
  isPausedState(): boolean {
    return this.isPaused;
  }

  /**
   * Clean up without uploading (for cleanup on unmount/cancel)
   */
  cleanup(): void {
    console.log("[AudioChunkUploader] Cleaning up without upload");

    if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
      this.mediaRecorder.stop();
      this.mediaRecorder.removeEventListener("dataavailable", () => {});
      this.mediaRecorder = null;
    }

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }

    this.audioChunks = [];
  }
}

/**
 * Hook-like function to create and manage AudioChunkUploader
 */
export const createAudioChunkUploader = (sessionId: number) => {
  return new AudioChunkUploader(sessionId);
};
